import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Loader2, Package, CheckCircle2, Truck, Home as HomeIcon, Clock } from "lucide-react";

const STEPS = ["placed", "processing", "shipped", "delivered"];
const STEP_META = {
  placed: { label: "Placed", icon: Clock },
  processing: { label: "Processing", icon: Package },
  shipped: { label: "Shipped", icon: Truck },
  delivered: { label: "Delivered", icon: HomeIcon },
};

function Timeline({ status }) {
  if (status === "cancelled") {
    return <p className="font-mono text-[11px] uppercase tracking-widest text-red-400">Order cancelled</p>;
  }
  const activeIdx = STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-4">
      {STEPS.map((s, i) => {
        const done = i <= activeIdx;
        const M = STEP_META[s];
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center ${done ? "bg-brand text-black" : "bg-zinc-800 text-zinc-500"}`}>
                <M.icon className="h-3.5 w-3.5" />
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-widest ${done ? "text-brand" : "text-zinc-600"}`}>{M.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${i < activeIdx ? "bg-brand" : "bg-zinc-800"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-10" data-testid="account-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-widest text-brand">Account</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight mt-1">Hey, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="font-mono text-xs text-zinc-500 mt-1">{user?.email}</p>
        </div>
      </div>

      <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Order History</h2>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : orders.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-950 p-12 text-center">
          <Package className="h-10 w-10 text-zinc-600 mx-auto" />
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">No orders yet</p>
          <Link to="/shop" className="inline-block mt-5 rounded-full bg-brand text-black px-6 py-2.5 text-xs font-bold uppercase tracking-wide active:scale-95 transition">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => (
            <div key={o.id} className="border border-zinc-800 bg-zinc-950 p-5" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-sm">#{o.order_no}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${o.payment_status === "paid" ? "bg-brand/15 text-brand" : "bg-zinc-800 text-zinc-400"}`}>
                    {o.payment_status === "paid" ? "Paid" : o.payment_status}
                  </span>
                  <span className="font-mono text-sm">{inr(o.total)}</span>
                </div>
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 pr-3">
                    <img src={it.image} alt={it.name} className="h-12 w-10 object-cover" />
                    <div>
                      <p className="text-xs">{it.name}</p>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{it.size} × {it.qty}</p>
                    </div>
                  </div>
                ))}
              </div>

              {o.payment_status === "paid" && <Timeline status={o.status} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
